import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarDays, DollarSign, Users, ClipboardCheck, AlertTriangle, Plus, Trash2, Loader2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

const statusColors = {
  confirmed: 'bg-[#2D6A4F] text-white',
  pending_payment: 'bg-[#D4A373] text-[#1F2A25]',
  completed: 'bg-[#1E3F33] text-white',
  checked_out: 'bg-[#457B9D] text-white',
  pending_inspection: 'bg-[#D4A373] text-[#1F2A25]',
  damage_reported: 'bg-[#BC4749] text-white',
  cancelled: 'bg-[#6B726A] text-white',
};

function AdminContent() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'manager', phone: '' });
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');
  const [enquiries, setEnquiries] = useState([]);

  const fetchData = async () => {
    try {
      const [sRes, bRes, uRes, dRes, eRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/bookings'),
        api.get('/admin/users'),
        api.get('/admin/deposits'),
        api.get('/admin/enquiries'),
      ]);
      setStats(sRes.data);
      setBookings(bRes.data);
      setUsers(uRes.data);
      setDeposits(dRes.data);
      setCleaners(uRes.data.filter(u => u.role === 'cleaner'));
      setEnquiries(eRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}/status`, { status: newStatus });
      fetchData();
    } catch {}
  };

  const assignCleaner = async (bookingId, cleanerId) => {
    try {
      await api.post(`/admin/bookings/${bookingId}/assign-cleaner`, { cleaner_id: cleanerId });
      fetchData();
    } catch {}
  };

  const depositAction = async (bookingId, action) => {
    try {
      await api.post(`/admin/deposits/${bookingId}/action`, { action });
      fetchData();
    } catch {}
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMsg('');
    try {
      await api.post('/admin/users', newUser);
      setNewUser({ email: '', password: '', name: '', role: 'manager', phone: '' });
      setMsg('User created successfully');
      fetchData();
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail));
    }
    setCreating(false);
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchData();
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#CC5A3D]" /></div>;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'bookings', label: 'Bookings' },
    { id: 'enquiries', label: `Enquiries (${enquiries.length})` },
    { id: 'deposits', label: 'Deposits' },
    { id: 'users', label: 'Users' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-3xl font-heading font-medium text-[#1F2A25] mb-6">Admin Dashboard</h1>

      <div className="flex gap-2 mb-6 border-b border-[#E6D5B8] pb-3">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm rounded-t transition-colors ${tab === t.id ? 'bg-[#1E3F33] text-white' : 'text-[#6B726A] hover:text-[#1F2A25]'}`} data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-stats">
          {[
            { icon: CalendarDays, label: 'Total Bookings', value: stats.total_bookings, color: '#1E3F33' },
            { icon: DollarSign, label: 'Revenue', value: `£${stats.total_revenue.toFixed(2)}`, color: '#2D6A4F' },
            { icon: ClipboardCheck, label: 'Pending Inspection', value: stats.pending_inspection, color: '#D4A373' },
            { icon: AlertTriangle, label: 'Damage Reports', value: stats.damage_reports, color: '#BC4749' },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-[#E6D5B8] p-5 rounded-lg" data-testid={`stat-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[#6B726A]">{s.label}</span>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <p className="text-2xl font-heading font-medium text-[#1F2A25]">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'bookings' && (
        <div className="bg-white border border-[#E6D5B8] rounded-lg overflow-hidden" data-testid="bookings-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EBE1]">
                <tr>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Guest</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Caravan</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Dates</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Amount</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Cleaner</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} className="border-t border-[#E6D5B8]" data-testid={`booking-row-${b.id}`}>
                    <td className="px-4 py-3 font-medium text-[#1F2A25]">{b.guest_name}</td>
                    <td className="px-4 py-3 text-[#6B726A]">{b.caravan_name}</td>
                    <td className="px-4 py-3 text-[#6B726A] whitespace-nowrap">{b.check_in} - {b.check_out}</td>
                    <td className="px-4 py-3 font-medium">£{b.total_price?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[b.status] || 'bg-gray-100 text-gray-600'}`}>{b.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Select onValueChange={(v) => assignCleaner(b.id, v)} value={b.cleaner_id || ''}>
                        <SelectTrigger className="w-32 h-8 text-xs border-[#E6D5B8]" data-testid={`assign-cleaner-${b.id}`}>
                          <SelectValue placeholder="Assign..." />
                        </SelectTrigger>
                        <SelectContent>
                          {cleaners.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {b.status === 'confirmed' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, 'checked_out')} className="text-xs h-7 border-[#E6D5B8]" data-testid={`checkout-btn-${b.id}`}>Check Out</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6B726A]">No bookings yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'enquiries' && (
        <div className="space-y-4" data-testid="enquiries-section">
          <p className="text-sm text-[#6B726A] mb-4">Enquiries from visitors who couldn't find available dates — leads for Sue's network.</p>
          {enquiries.length === 0 && <p className="text-[#6B726A] py-8 text-center bg-white border border-[#E6D5B8] rounded-lg">No enquiries yet</p>}
          {enquiries.map(eq => (
            <div key={eq.id} className="bg-white border border-[#E6D5B8] p-5 rounded-lg" data-testid={`enquiry-${eq.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-medium text-[#1F2A25]">{eq.name}</p>
                  <p className="text-sm text-[#6B726A]">{eq.email} {eq.phone ? `· ${eq.phone}` : ''}</p>
                </div>
                <Badge variant="secondary" className={`capitalize text-xs ${eq.status === 'new' ? 'bg-[#CC5A3D] text-white' : 'bg-[#F0EBE1] text-[#1E3F33]'}`}>{eq.status}</Badge>
              </div>
              <p className="text-sm text-[#1F2A25] mb-1">Dates: <strong>{eq.arrival_date} → {eq.departure_date}</strong> · {eq.num_guests} guest(s)</p>
              {eq.message && <p className="text-sm text-[#6B726A] italic">"{eq.message}"</p>}
              <p className="text-xs text-[#6B726A] mt-2">{new Date(eq.created_at).toLocaleString('en-GB')}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'deposits' && (
        <div className="space-y-4" data-testid="deposits-section">
          {deposits.length === 0 && <p className="text-[#6B726A]">No deposits requiring action</p>}
          {deposits.map(d => (
            <div key={d.id} className="bg-white border border-[#E6D5B8] p-5 rounded-lg flex items-center justify-between" data-testid={`deposit-${d.id}`}>
              <div>
                <p className="font-medium text-[#1F2A25]">{d.guest_name} - {d.caravan_name}</p>
                <p className="text-sm text-[#6B726A]">Deposit: £{d.deposit_amount?.toFixed(2)} | Status: {d.deposit_status}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => depositAction(d.id, 'release')} className="bg-[#2D6A4F] text-white text-xs h-8" data-testid={`release-deposit-${d.id}`}>Release</Button>
                <Button size="sm" onClick={() => depositAction(d.id, 'capture_full')} variant="destructive" className="text-xs h-8" data-testid={`capture-deposit-${d.id}`}>Capture</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-6" data-testid="users-section">
          <form onSubmit={createUser} className="bg-white border border-[#E6D5B8] p-5 rounded-lg">
            <h3 className="text-lg font-heading font-medium text-[#1F2A25] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Create User</h3>
            {msg && <p className="text-sm text-[#6B726A] mb-3" data-testid="user-msg">{msg}</p>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="mt-1 border-[#E6D5B8]" required data-testid="new-user-name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} type="email" className="mt-1 border-[#E6D5B8]" required data-testid="new-user-email" />
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} type="password" className="mt-1 border-[#E6D5B8]" required data-testid="new-user-password" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                  <SelectTrigger className="mt-1 border-[#E6D5B8]" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="cleaner">Cleaner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={newUser.phone} onChange={e => setNewUser({...newUser, phone: e.target.value})} className="mt-1 border-[#E6D5B8]" data-testid="new-user-phone" />
              </div>
            </div>
            <Button type="submit" disabled={creating} className="bg-[#CC5A3D] text-white hover:bg-[#B34A30] rounded-sm" data-testid="create-user-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Create User
            </Button>
          </form>

          <div className="bg-white border border-[#E6D5B8] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EBE1]">
                <tr>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Name</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-[#6B726A] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.email} className="border-t border-[#E6D5B8]" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-[#1F2A25]">{u.name}</td>
                    <td className="px-4 py-3 text-[#6B726A]">{u.email}</td>
                    <td className="px-4 py-3"><Badge variant="secondary" className="capitalize bg-[#F0EBE1] text-[#1E3F33]">{u.role}</Badge></td>
                    <td className="px-4 py-3">
                      {u.role !== 'owner' && u.id && (
                        <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)} className="text-[#BC4749] hover:text-red-700 h-7" data-testid={`delete-user-${u.email}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== 'owner')) navigate('/login');
  }, [user, loading, navigate]);

  if (loading || !user) return null;

  return (
    <DashboardLayout>
      <AdminContent />
    </DashboardLayout>
  );
}
